import { getProfileDataThunk, paymentThunk } from '../../../../../store/workspace/thunks';
import { PlusIcon, CreditCardIcon, SquareCheckIcon } from '../../../../../assets/icons';
import { selectExtraServiceDetail } from '../../../../../store/workspace/selectors';
import { xhrDefaultMessage } from '../../../../../configs/notification';
import { useDispatch, useSelector } from '../../../../../store/store';
import { PaymentMethod } from '../../../../../store/workspace/types';
import { setNotification } from '../../../../../store/general/slice';
import CheckBox from '../../../../../components/CheckBox';
import { useEffect, useState } from 'react';
import Spinner from '../../../../../components/Spinner';
import styles from './style.module.scss';
import Modal from '../../../../../components/Modal';
import {
    setAddNewCardModal,
    setExtraServicesPaymentModal,
} from '../../../../../store/workspace/slice';

type Step = 'SelectPaymentMethod' | 'Done';

const GENERIC_ERROR = 'Something went wrong,Please try again...';

/** Checkout of the extra job posts / applicants / seats bought elsewhere. */
export default function ExtraServicesPayment() {
    const dispatch = useDispatch();
    const serviceDetail = useSelector(selectExtraServiceDetail);
    const [fetching, setFetching] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [submitingPayment, setSubmitingPayment] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [step, setStep] = useState<Step>('SelectPaymentMethod');
    const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);

    useEffect(() => {
        setFetching(true);
        dispatch(paymentThunk({ action: 'get_payment_methods' }))
            .unwrap()
            .then(setPaymentMethods)
            .catch(() => {
                dispatch(setNotification([GENERIC_ERROR, 'error']));
            })
            .finally(() => {
                setFetching(false);
            });
    }, []);

    const closeModal = () => dispatch(setExtraServicesPaymentModal(false));

    const submitPayment = () => {
        if (!serviceDetail || !selectedMethod) return;

        setErrorMessage('');
        setSubmitingPayment(true);

        dispatch(paymentThunk({
            action: 'add_payment',
            add: serviceDetail.type,
            sub: serviceDetail.sub,
            pmId: selectedMethod.id,
        }))
            .unwrap()
            .then(() => {
                setStep('Done');
                dispatch(getProfileDataThunk());
                dispatch(setNotification(['payment has been successfully confirmed', 'success']));
            })
            .catch((error) => {
                setErrorMessage(error?.message || xhrDefaultMessage);
                dispatch(setNotification([GENERIC_ERROR, 'error']));
            })
            .finally(() => {
                setSubmitingPayment(false);
            });
    };

    const next = () => {
        if (step === 'Done') {
            closeModal();
            return;
        }

        submitPayment();
    };

    return (
        <Modal
            size='sm'
            closeFunc={closeModal}
            childClassName={styles.wrapper}
            icon={step === 'Done' ? <SquareCheckIcon /> : <CreditCardIcon />}
            title={step === 'Done' ? 'Successfull payment' : 'Choose a card to continue'}
            actions={step === 'SelectPaymentMethod' ? 'both' : 'confirm'}
            dismissButton={{
                text: 'Cancel',
                type: 'Outline-Primary',
                onClick: closeModal,
            }}
            confirmButton={{
                text: step === 'SelectPaymentMethod' ? 'Pay' : 'Confirm',
                type: 'Primary',
                onClick: next,
                disabled: !selectedMethod,
                loading: submitingPayment,
            }}
        >
            {step === 'SelectPaymentMethod' && <>
                <span>Your Cards</span>
                {!fetching && paymentMethods?.map(method => <div
                    key={method.id}
                    className={styles.card}
                    onClick={() => setSelectedMethod(method)}
                >
                    <CheckBox
                        checked={selectedMethod?.id === method.id}
                        onClick={() => setSelectedMethod(method)}
                    />
                    <div className={styles.card_info}>
                        <span>{method.card.brand}</span>
                        <span>Credit card ending in **** {method.card.last4}</span>
                    </div>
                </div>)}
                {fetching && <Spinner />}
                <div
                    className={styles.add_new_card}
                    onClick={() => dispatch(setAddNewCardModal(true))}
                >
                    <PlusIcon />
                    <span>Add new credit or debit card</span>
                </div>
            </>}

            {errorMessage && <span className={styles.error}>{errorMessage}</span>}

            {step === 'Done' && <span className={styles.successfull}>
                Your payment has been successfully confirmed.
            </span>}
        </Modal>
    );
};
